import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from '@phosphor-icons/react';

const STORAGE_KEY = 'ecocasse_cookie_consent_v1';
export const COOKIE_RESET_EVENT = 'ecocasse:cookie-reset';
export const ANALYTICS_CONSENT_EVENT = 'ecocasse:analytics-consent';

export const hasAnalyticsConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}').choice === 'all';
  } catch {
    return false;
  }
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY);
    if (!consent) {
      // Show banner after small delay so it doesn't compete with PWA prompt
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  // Listen for global reset event (from "Modifier mes préférences" button)
  useEffect(() => {
    const handler = () => {
      setShowDetails(false);
      setVisible(true);
    };
    window.addEventListener(COOKIE_RESET_EVENT, handler);
    return () => window.removeEventListener(COOKIE_RESET_EVENT, handler);
  }, []);

  const saveConsent = (choice) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ choice, date: new Date().toISOString() })
    );
    if (choice === 'all') window.dispatchEvent(new Event(ANALYTICS_CONSENT_EVENT));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-[60]"
          data-testid="cookie-banner"
        >
          <div className="bg-white border border-gray-200 shadow-2xl p-5 md:p-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 bg-[#E63946]/10 flex items-center justify-center flex-shrink-0">
                <Cookie size={20} className="text-[#E63946]" weight="fill" />
              </div>
              <div className="flex-1">
                <h3 className="font-['Outfit'] font-bold text-gray-900 text-base mb-1">
                  Respect de votre vie privée
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Nous utilisons des cookies essentiels pour le bon fonctionnement du site (authentification,
                  panier) et, avec votre accord, des cookies d'analyse pour améliorer nos services.
                </p>
              </div>
            </div>

            {showDetails && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-3 mb-2 text-xs text-gray-500 space-y-2 bg-gray-50 p-3 border border-gray-100"
              >
                <div>
                  <span className="font-semibold text-gray-700">Essentiels (toujours actifs)</span> :
                  session, préférences de langue, panier de devis.
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Analyse (optionnel)</span> :
                  statistiques de fréquentation (pages visitées et adresse IP publique), avec conservation limitée à 30 jours.
                </div>
                <div>
                  Vos photos uploadées pour l'analyse de pièces sont traitées de façon
                  sécurisée et ne sont conservées que le temps nécessaire au traitement de votre demande.
                </div>
              </motion.div>
            )}

            <div className="flex flex-col gap-2 mt-4">
              <div className="flex gap-2">
                <button
                  onClick={() => saveConsent('all')}
                  className="flex-1 py-3 bg-[#E63946] text-white font-bold text-xs uppercase tracking-wide hover:opacity-90 transition-opacity"
                  data-testid="cookie-accept-all"
                >
                  Tout accepter
                </button>
                <button
                  onClick={() => saveConsent('essential')}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 font-bold text-xs uppercase tracking-wide hover:bg-gray-50 transition-colors"
                  data-testid="cookie-essential-only"
                >
                  Essentiels uniquement
                </button>
              </div>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-gray-500 hover:text-gray-700 text-xs underline self-start"
                data-testid="cookie-details-toggle"
              >
                {showDetails ? 'Masquer le détail' : 'Voir le détail'}
              </button>
            </div>

            <button
              onClick={() => saveConsent('essential')}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 transition-colors"
              aria-label="Fermer"
              data-testid="cookie-close"
            >
              <X size={18} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
